/**
 * Build/version metadata for the application footer (support triage).
 *
 * `__APP_VERSION__` / `__APP_GIT_SHA__` / `__APP_BUILD_TIME__` are injected at build
 * time by `vite.config.ts` `define`. They are guarded with `typeof` fallbacks so this
 * module is safe under any runner (and if the `define` ever changes).
 */
function injected(value: () => string, fallback: string): string {
  try {
    const resolved = value();
    return resolved && resolved.length > 0 ? resolved : fallback;
  } catch {
    return fallback;
  }
}

export const appVersion: string = injected(
  () => (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''),
  '0.0.0',
);

export const gitSha: string = injected(
  () => (typeof __APP_GIT_SHA__ !== 'undefined' ? __APP_GIT_SHA__ : ''),
  'local',
);

export const buildTime: string = injected(
  () => (typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : ''),
  '',
);

/** Deployment environment label; falls back to Vite's dev/prod signal. */
export const appEnvironment: string = (
  import.meta.env.VITE_APP_ENV ?? (import.meta.env.DEV ? 'development' : 'production')
).toLowerCase();

export const isProduction: boolean = appEnvironment === 'production' || appEnvironment === 'prod';

/** Human-friendly environment label for the footer badge. */
export function environmentLabel(): string {
  switch (appEnvironment) {
    case 'production':
    case 'prod':
      return 'Production';
    case 'uat':
    case 'staging':
      return 'UAT';
    case 'development':
    case 'dev':
      return 'Development';
    default:
      return appEnvironment.charAt(0).toUpperCase() + appEnvironment.slice(1);
  }
}

/** Short "v{version} · {sha} · {date}" string for the footer. `date` omitted if unknown. */
export function versionSummary(): string {
  const parts = [`v${appVersion}`, gitSha];
  const date = buildDate();
  if (date) {
    parts.push(date);
  }
  return parts.join(' \u00b7 ');
}

/** Build timestamp as a YYYY-MM-DD date, or '' when unknown/unparseable. */
export function buildDate(): string {
  if (!buildTime) {
    return '';
  }
  const parsed = new Date(buildTime);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
}

/** Full, copyable diagnostics string for support tickets (no PII). */
export function buildDiagnostics(): string {
  const userAgent =
    typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : 'unknown';
  return [
    'TAG Operations',
    `version: v${appVersion}`,
    `commit: ${gitSha}`,
    `build: ${buildTime || 'unknown'}`,
    `environment: ${environmentLabel()}`,
    `userAgent: ${userAgent}`,
  ].join('\n');
}
